import React, { FC, useId } from 'react';
import { connect } from 'react-redux';

import ComposeChecklist from '../../../components/Compose/ComposeChecklist';
import ComposeSection from '../../../components/MUI/Compose/ComposeSection';
import translate from '../../../i18n/translate';
import { ChecklistDataDraft, Text } from '../../../models/checklist';
import { ComposeModeParam } from '../../../models/pageParams';

import useComposeLinking from '../hooks/useComposeLinking';

import { mapDispatchToProps, mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Managing checklist in the compose form
 *
 * @param props                           Props passed to the container
 * @param props.dataChecklistItemsSaved   Saved items
 * @param props.requestChecklistCreate    Add a new checklist item
 * @param props.requestChecklistDelete    Remove a checklist item
 * @param props.requestChecklistDeleteAll Remove all checklist items
 * @param props.requestChecklistReorder   Reorder an item
 * @param props.requestChecklistSave      Request current -> saved data move
 * @returns                               The container itself
 */
const ComposeMetaChecklistContainer: FC<Props> = ({
  dataChecklistItemsSaved,
  requestChecklistCreate,
  requestChecklistDelete,
  requestChecklistDeleteAll,
  requestChecklistReorder,
  requestChecklistSave,
}) => {
  const mode: ComposeModeParam = 'checklist';
  const { isActive, linkToggleProps } = useComposeLinking(mode);
  const idHeading = useId();

  const textHeading = translate('MESSAGE__HEADING__CHECKLIST');

  /**
   * Handle adding new checklist item
   *
   * @param text The value of new checklist item
   */
  const requestAddNewItem = (text: Text) => {
    requestChecklistCreate(text);
    requestChecklistSave();
  };

  /**
   * Handle deleting checklist item
   *
   * @param position The position of checklist item to be deleted
   */
  const requestRemoveItem = (position: number) => {
    requestChecklistDelete(position);
    requestChecklistSave();
  };

  /**
   * Handle reordering of checklist items
   *
   * @param items Items in new order
   */
  const requestReorderItems = (items: ChecklistDataDraft[]) => {
    requestChecklistReorder(items);
    requestChecklistSave();
  };

  return (
    <ComposeSection
      idHeading={idHeading}
      inputCustom={null}
      isActive={isActive}
      linkToggleProps={linkToggleProps}
      textHeading={textHeading}
      value={textHeading}
    >
      <ComposeChecklist
        items={dataChecklistItemsSaved}
        requestCreate={requestAddNewItem}
        requestDelete={requestRemoveItem}
        requestDeleteAll={requestChecklistDeleteAll}
        requestReorder={requestReorderItems}
      />
    </ComposeSection>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ComposeMetaChecklistContainer);
